import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const EstimationDetailsListing = () => {
  const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const getMonthStart = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    return first.toISOString().slice(0, 10);
  };

  const getToday = () => new Date().toISOString().slice(0, 10);

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState(getMonthStart());
  const [toDate, setToDate] = useState(getToday());

  // Dropdown
  const [projectName, setProjectName] = useState("");
  const [estimationCode, setEstimationCode] = useState("");

  const [projectList, setProjectList] = useState([]);
  const [estCodeList, setEstCodeList] = useState([]);

  const [loadingDD, setLoadingDD] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / pageSize);

  const [selectedIds, setSelectedIds] = useState([]);

  const loadDropdowns = async () => {
    try {
      setLoadingDD(true);

      const res = await axios.get(
        `${BASE_URL}/api/estimationDetails/dropdowns`,
        {
          headers: commonHeaders,
        },
      );

      if (res.data?.success) {
        setProjectList(res.data.projects || []);
        setEstCodeList(res.data.estimationCodes || []);
      } else {
        toast.error(res.data?.message || "Failed to load dropdowns");
      }
    } catch (err) {
      console.error(err);
      toast.error("Dropdown load error");
    } finally {
      setLoadingDD(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
  }, []);

  const fetchList = async (pageToLoad = 1, newPageSize = pageSize) => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/estimationDetails/search`, {
        params: {
          fromDate,
          toDate,
          projectName,
          estimationCode,
          page: pageToLoad,
          pageSize: newPageSize,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setRows(res.data.data || []);
        setTotal(res.data.pagination?.total || 0);
        setPage(res.data.pagination?.page || pageToLoad);
        setSelectedIds([]);
      } else {
        toast.error(res.data?.message || "Search failed");
        setRows([]);
        setTotal(0);
      }
    } catch (err) {
      console.error(err);
      toast.error("Search error");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchList(1);
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setProjectName("");
    setEstimationCode("");
  };

  const handleSelectRow = (estimationId, checked) => {
    setSelectedIds((prev) =>
      checked
        ? [...prev, estimationId]
        : prev.filter((id) => id !== estimationId),
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one record to delete");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`,
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        `${BASE_URL}/api/estimationDetails/delete`,
        { ids: selectedIds },
        { headers: commonHeaders },
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      if (res.data.blockedIds?.length) {
        toast.warn(
          `Some records could not be deleted: ${res.data.blockedIds.join(", ")}`,
        );
      }

      toast.success(res.data.message || "Deleted successfully");
      fetchList(page);
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(
        err?.response?.data?.message || "Server error while deleting",
      );
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNew = () => {
    navigate("/OrderManagement/EstimationDetails");
    toast.success("Add New BOQ clicked!");
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">ESTIMATION DETAILS</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Estimation Details Listing
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showFilters && (
              <div
                className={`transition-all duration-300 ease-in-out ${
                  showFilters
                    ? "max-h-none opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className={`p-6 border-t ${
                    isDark
                      ? "bg-gray-800 border-gray-700"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        From Date
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        To Date
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Project Name
                      </label>

                      <select
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        disabled={loadingDD}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">
                          {loadingDD ? "Loading..." : "Select"}
                        </option>
                        {projectList.map((p) => (
                          <option
                            key={`${p.estimationId}-${p.projectName}`}
                            value={p.projectName}
                          >
                            {p.projectName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Estimation Code
                      </label>

                      <select
                        value={estimationCode}
                        onChange={(e) => setEstimationCode(e.target.value)}
                        disabled={loadingDD}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">
                          {loadingDD ? "Loading..." : "Select"}
                        </option>
                        {estCodeList.map((c) => (
                          <option
                            key={`${c.estimationId}-${c.estimationCode}`}
                            value={c.estimationCode}
                          >
                            {c.estimationCode}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center mb-6 gap-6">
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? `${buttonClass} ${hoverClass} text-white`
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                    }`}
                  >
                    Reset
                  </button>

                  <button
                    onClick={handleAddNew}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? `${buttonClass} ${hoverClass} text-white`
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                  >
                    <FaPlus />
                    Add New
                  </button>
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Estimation Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Project Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Date
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Customer Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Customer Address
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Remarks
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Status
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Select
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-6">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-6">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={r.estimationId}>
                        <td className="px-4 py-3 text-center">
                          {(page - 1) * pageSize + (idx + 1)}
                        </td>

                        <td
                          className="px-4 py-3 text-center text-blue-600 cursor-pointer"
                          onClick={() =>
                            navigate(
                              `/OrderManagement/EstimationDetails/${
                                r.preEstimationId || r.estimationId
                              }`,
                            )
                          }
                          title="Click to view / update"
                        >
                          {r.estimationCode}
                        </td>

                        <td className="px-4 py-3 text-center text-blue-600 cursor-pointer">
                          {r.projectName}
                        </td>

                        <td className="px-4 py-3 text-center">{r.date}</td>
                        <td className="px-4 py-3 text-center">
                          {r.customerName}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {r.customerAddress}
                        </td>
                        <td className="px-4 py-3 text-center">{r.remarks}</td>
                        <td className="px-4 py-3 text-center">{r.status}</td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.estimationId)}
                            onChange={(e) =>
                              handleSelectRow(r.estimationId, e.target.checked)
                            }
                            className={`rounded focus:ring-2 focus:ring-blue-500 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-blue-600"
                                : "bg-white border-gray-300 text-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-4">
                <div
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Total: {total} | Page {page} of {totalPages || 1}
                </div>

                <button
                  onClick={handleDeleteSelected}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                    isDark
                      ? "bg-red-700 hover:bg-red-600 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                >
                  Delete Selected
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1 || loading}
                  onClick={() => fetchList(page - 1)}
                  className={`px-3 py-1 rounded border ${
                    isDark ? "border-gray-600 text-white" : "border-gray-300"
                  } disabled:opacity-50`}
                >
                  Prev
                </button>

                <button
                  disabled={page >= totalPages || loading}
                  onClick={() => fetchList(page + 1)}
                  className={`px-3 py-1 rounded border ${
                    isDark ? "border-gray-600 text-white" : "border-gray-300"
                  } disabled:opacity-50`}
                >
                  Next
                </button>

                <select
                  value={pageSize}
                  disabled={loading}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPageSize(newSize);
                    setPage(1);
                    fetchList(1, newSize);
                  }}
                  className={`px-2 py-1 rounded border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {[10, 20, 50, 100].map((s) => (
                    <option key={s} value={s}>
                      {s} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default EstimationDetailsListing;
